import {ChangeDetectorRef, Component, DoCheck, OnDestroy, OnInit} from '@angular/core';
import {ReactiveFormsModule, UntypedFormArray, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {EditPermissions, LocationMessage, NodeBuilder} from '../../../../../../../shared/models';
import {additionalDataErrorMessage, SendTemplateNode, TemplateForm} from './sendTemplate';
import {SendTemplateService, WaBusinessAccounts, WaIntegrations} from './send-template.service';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {WaTemplate, WaTemplateService} from '../../../../../template/wa-template-preview/wa-template.service';
import {newStepValue} from '../../../../../services/flowBuilder/utils/constants';
import isEmpty from 'lodash/isEmpty';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';
import {additionalDataMaxLength, isValidVariable, required} from '../../../../../../../shared/utils';
import {MatOption} from '@angular/material/core';
import {SharedService} from '../../../../../../../core/services/store/shared-store/shared-storage.service';
import {firstValueFrom, of, Subscription} from 'rxjs';
import {getEditTypes} from '../../../delegatorUtils';
import {FlowBuilderService} from '../../../../../services/flowBuilder/flow-builder.service';
import cloneDeep from 'lodash/cloneDeep';
import {
    ClickatellExpansionPanelComponent
} from '../../../../../../../shared/components/clickatell-expansion-panel/clickatell-expansion-panel.component';
import {MatExpansionPanelTitle} from '@angular/material/expansion';
import {KeyValuePipe, NgClass, NgStyle, NgTemplateOutlet} from '@angular/common';
import {ActiveFormDirective} from '../../../../../../../shared/directives/active-form/active-form.directive';
import {MatInput} from '@angular/material/input';
import {MatOption as MatOption_1} from '@angular/material/select';
import {LoadingIconComponent} from '../../../../../../../shared/components/loading-icon/loading-icon.component';
import {CMSComponent} from '../../../../../../../shared/components/cms/cms.component';
import {NextSelectorComponent} from '../../../../json-to-from/next-selector/next-selector.component';
import {WaTemplatePreviewComponent} from '../../../../../template/wa-template-preview/wa-template-preview.component';
import {ShowLocationComponent} from '../../../../preview/show-location/show-location.component';
import {SelectMediaComponent} from '../../../../select-media/select-media.component';
import {ArrayTemplateFilterPipe} from '../../../../../../../shared/utils/pipes/ArrayTemplateFilterPipe';
import {GetObjectKeysPipe} from '../../../../../../../core/services/roles/get-object-keys.pipe';

const NODE_TYPE = 'sendTemplate';

@Component({
    selector: 'app-send-template',
    templateUrl: './send-template.component.html',
    styleUrls: ['./send-template.component.scss'],
    imports: [ClickatellExpansionPanelComponent, MatExpansionPanelTitle, NgTemplateOutlet, ActiveFormDirective, MatButtonToggleGroup, MatButtonToggle, MatInput, MatAutocompleteTrigger, MatAutocomplete, MatOption_1, NgStyle, LoadingIconComponent, NgClass, CMSComponent, NextSelectorComponent, WaTemplatePreviewComponent, ShowLocationComponent, SelectMediaComponent, KeyValuePipe, ArrayTemplateFilterPipe, GetObjectKeysPipe, ReactiveFormsModule]
})

export class SendTemplateComponent extends NodeBuilder implements OnInit, DoCheck, OnDestroy {

    actionParamsForm: UntypedFormGroup = undefined;
    templateContentForm: UntypedFormGroup = undefined;

    waBusinessAccounts: WaBusinessAccounts[] = undefined;
    waIntegrations: WaIntegrations[] = undefined;
    waTemplates: WaTemplate[] = undefined;

    waBusinessAccountsFiltered: WaBusinessAccounts[] = [];
    waIntegrationsFiltered: WaIntegrations[] = [];
    waTemplatesFiltered: WaTemplate[] = [];

    waIntegrationsLoading = false;
    waTemplatesLoading = false;
    template: WaTemplate = undefined;
    templateIdCheck: string = undefined;

    loadInitData = false;

    accountsError = false;
    integrationsError = false;
    templatesError = false;


    stepUniqueID = '';
    selectedMediaType: 'assets' | 'variable' = 'assets';
    private nameValueChanges$: Subscription;

    isLocationPicker = false;

    get getBodyVars(): UntypedFormArray {
        return this.templateContentForm.controls['bodyVars'] as UntypedFormArray;
    }

    get getWebsiteUrls(): UntypedFormArray {
        return this.templateContentForm.controls['websiteUrls'] as UntypedFormArray;
    }

    get businessAccountId(): UntypedFormControl {
        return this.actionParamsForm.controls['businessAccountId'] as UntypedFormControl;
    }

    get integrationId(): UntypedFormControl {
        return this.actionParamsForm.controls['integrationId'] as UntypedFormControl;
    }

    get templateId(): UntypedFormControl | undefined {
        return this.actionParamsForm?.controls['templateId'] as UntypedFormControl;
    }


    constructor(private sendTemplateService: SendTemplateService,
                private waTemplateService: WaTemplateService,
                private sharedService: SharedService,
                private changeDetectorRef: ChangeDetectorRef,
                private flowBuilder: FlowBuilderService) {
        super(NODE_TYPE);
    }

    ngOnInit(): void {
        this.sendTemplateService.get_accounts().pipe(
            tap(data => {
                this.accountsError = data.length > 0 && data[0].id === null;
                this.waBusinessAccounts = data;
            }),
            tap(data => this.waBusinessAccountsFiltered = data.slice()),
            catchError(_ => {
                this.accountsError = true;
                this.waBusinessAccounts = [];
                this.waBusinessAccountsFiltered = [];
                return of([]);
            })
        ).subscribe();
    }

    ngDoCheck(): void {
    }

    ngOnDestroy(): void {
        this.nameValueChanges$?.unsubscribe();
    }

    public wabaFilter(element: HTMLInputElement) {
        const filterValue = element.value;
        if (isEmpty(this.waBusinessAccounts)) {
            this.waBusinessAccountsFiltered = [];
            return;
        }
        this.clearWaIntegrations();
        this.waBusinessAccountsFiltered = this.waBusinessAccounts?.filter(option => option.wabaName.includes(filterValue));
    }

    public waIntegrationsFilter(element: HTMLInputElement) {
        const filterValue = element.value;
        if (isEmpty(this.waIntegrations)) {
            this.waIntegrationsFiltered = [];
            return;
        }
        this.clearWaTemplate();
        this.waIntegrationsFiltered = this.waIntegrations?.filter(option => option.integrationName.includes(filterValue));
    }

    public waTemplatesFilter(element: HTMLInputElement) {
        const filterValue = element.value;
        if (isEmpty(this.waTemplates)) {
            this.waTemplatesFiltered = [];
            return;
        }
        this.waTemplatesFiltered = this.waTemplates?.filter(option => option.name.includes(filterValue));
    }


    set buildForm(stepData: any) {
        const {actionParamsForm, templateContentForm} = SendTemplateNode.toForm(stepData, this.canEdit);
        this.actionParamsForm = actionParamsForm;

        if (this.actionParamsForm.get('success').value === newStepValue) {
            this.actionParamsForm.get('success').setValue('step' + this.getNextStep());
        }

        if (this.actionParamsForm.get('error').value === newStepValue) {
            this.actionParamsForm.get('error').setValue('step' + this.getNextStep());
        }

        this.stepUniqueID = stepData.metadata?.stepUniqueID ? stepData.metadata?.stepUniqueID : '';

        if (!isEmpty(stepData)) {
            this.loadInitialData(templateContentForm, stepData);
        }

    }

    private loadInitialData(templateContentForm: TemplateForm, stepData: any) {
        const businessAccountId = this.businessAccountId.value;
        const integrationId = this.integrationId.value;

        if (isEmpty(businessAccountId) && isEmpty(integrationId)) {
            return
        }

        this.waIntegrationsLoading = true;
        this.waTemplatesLoading = true;
        this.loadInitData = true;

        this.sendTemplateService.get_integrations(businessAccountId).pipe(
            catchError(_ => {
                    this.integrationsError = true;
                    this.waIntegrationsLoading = false;
                    this.waTemplatesLoading = false;
                    return of([]); // Return empty array to continue the stream
                }
            ),
            switchMap(waIntegrations => {
                this.waIntegrations = waIntegrations;
                this.waIntegrationsFiltered = waIntegrations.slice();
                return this.sendTemplateService.get_templates(integrationId).pipe(
                    catchError(_ => {
                        this.templatesError = true;
                        return of([]);
                    }),
                    tap(data => this.waTemplates = data),
                    tap(data => this.waTemplatesFiltered = data.slice())
                );
            }),
        ).subscribe({
            next: async () => {
                this.waIntegrationsLoading = false;
                this.waTemplatesLoading = false;

                switch (this.configMode()) {
                    case 'default':
                        await this.initDefault(stepData);
                        break;
                    case 'json':
                        this.jsonMode();
                        break;
                }

                this.templateContentForm.patchValue(templateContentForm);
                this.flowBuilder.setNodeValid(this.nodeFlowDetails.stepId, this.isNodeValid);
                this.loadInitData = false;
                this.setMediaType();
            }
        });
    }

    private async initDefault(stepData: any) {
        this.template = this.waTemplates.find(x => x.id === this.templateId?.value);

        if (!this.template && this.templateId?.value) {
            this.template = await firstValueFrom(this.waTemplateService.get_template(this.templateId?.value));
        }

        const canEdit: EditPermissions = cloneDeep(this.canEdit);
        canEdit.canEdit = this.isValidTemplate ? this.canEdit.canEdit : false;
        this.templateIdCheck = this.templateId?.value;
        const templateFormData = SendTemplateNode.toTemplate(this.template);
        this.templateContentForm = SendTemplateNode.buildTemplate(canEdit, templateFormData);
        if (this.templateContentForm.controls['suggestedResponse'] &&
            !isEmpty(stepData) && stepData['action']['actionParams']['parameters']['buttons'] !== undefined &&
            stepData['action']['actionParams']['parameters']['buttons']['suggestedResponse'] !== undefined) {

            Object.keys(stepData['action']['actionParams']['parameters']['buttons']['suggestedResponse']).forEach(key => {
                const abstractControl = this.templateContentForm.controls['suggestedResponse'].get(key);
                if (abstractControl) {
                    abstractControl.addValidators(
                        additionalDataMaxLength(this.actionParamsForm.get('name'), this.sharedService.getEntityId(), additionalDataErrorMessage));
                }
            });

            this.nameValueChanges$?.unsubscribe();
            this.nameValueChanges$ = this.actionParamsForm.get('name').valueChanges.pipe(
                tap(() => {
                    Object.keys(stepData['action']['actionParams']['parameters']['buttons']['suggestedResponse']).forEach(key => {
                        const abstractControl = this.templateContentForm.controls['suggestedResponse'].get(key);
                        if (abstractControl) {
                            abstractControl.updateValueAndValidity();
                        }

                    });
                })
            ).subscribe();
        }
    }

    get getStepData(): {} {
        const stepData = new SendTemplateNode();
        stepData.addActionParams(this.actionParamsForm.getRawValue());
        if (this.templateContentForm) {
            stepData.addTemplateParameters(this.templateContentForm.getRawValue());
        }

        const nodeData = stepData.nodeData;
        if (this.stepUniqueID !== '') {
            nodeData['metadata'].stepUniqueID = this.stepUniqueID;
        }
        return nodeData;
    }

    get isNodeValid(): boolean {
        this.isValidControl('integration', this.integrationId, this.findWaIntegrations);

        this.isValidControl('business account', this.businessAccountId, this.findWaBusinessAccounts);

        if (this.actionParamsForm.get('configMode').value === 'default') {
            this.isValidControl('template', this.templateId, this.findWaTemplate);
        }

        this.actionParamsForm.markAllAsTouched();

        if (!this.templateContentForm) {
            return false;
        }

        this.templateContentForm.markAllAsTouched();
        const isValid = this.templateContentForm.valid && this.actionParamsForm.valid && this.isValidTemplate;
        return isValid;
    }

    get isValidTemplate() {
        // In JSON mode, template validation doesn't apply
        if (this.configMode() === 'json') {
            return true;
        }

        if (this.template) {
            return this.template.status === 'VALIDATED';
        }

        return false;
    }

    waBusinessAccountsChange(id: string) {

        if (isEmpty(id)) {
            return;
        }

        this.waIntegrationsLoading = true;
        this.integrationsError = false;
        this.templatesError = false;
        this.clearWaIntegrations();

        this.sendTemplateService.get_integrations(id).pipe(
            tap(data => this.waIntegrations = data),
            tap(data => this.waIntegrationsFiltered = data.slice()),
            catchError(_ => {
                this.integrationsError = true;
                this.waIntegrations = [];
                this.waIntegrationsFiltered = [];
                return of([]);
            })
        ).subscribe(() => this.waIntegrationsLoading = false);
    }

    waIntegrationsChange(id: string) {

        if (isEmpty(id)) {
            return;
        }

        this.waTemplatesLoading = true;
        this.templatesError = false;
        this.clearWaTemplate();

        this.sendTemplateService.get_templates(id).pipe(
            tap(data => this.waTemplates = data),
            tap(data => this.waTemplatesFiltered = data.slice()),
            catchError(_ => {
                this.templatesError = true;
                this.waTemplates = [];
                this.waTemplatesFiltered = [];
                return of([]);
            })
        ).subscribe(() => this.waTemplatesLoading = false);
    }

    waTemplatesChange(id: string) {
        if (isEmpty(id) || this.templateIdCheck === id) {
            return;
        }
        this.templateIdCheck = id;
        this.templateContentForm = undefined;
        this.changeDetectorRef.detectChanges();
        this.template = this.waTemplates.find(x => x.id === id);
        const templateFormData = SendTemplateNode.toTemplate(this.template);
        this.templateContentForm = SendTemplateNode.buildTemplate(this.canEdit, templateFormData);

        if (this.template.templateContent.buttons && this.template.templateContent.buttons.quickReply) {
            for (let i = 1; i <= this.template.templateContent.buttons.quickReply.length; i++) {
                this.templateContentForm.controls['suggestedResponse'].get(i.toString()).addValidators(
                    additionalDataMaxLength(this.actionParamsForm.get('name'), this.sharedService.getEntityId(), additionalDataErrorMessage));
            }
        }

        this.setMediaType();

        // will handle new templates/nodes
        if (this.template.templateContent.buttons && this.template.templateContent.buttons.quickReply) {
            this.nameValueChanges$?.unsubscribe();
            this.nameValueChanges$ = this.actionParamsForm.get('name').valueChanges.pipe(
                tap(() => {
                    for (let i = 1; i <= this.template.templateContent.buttons.quickReply.length; i++) {
                        this.templateContentForm.controls['suggestedResponse'].get(i.toString()).updateValueAndValidity();
                    }
                })
            ).subscribe();
        }

    }

    waBusinessAccountsDisplay(id: string): string {
        if (this.waBusinessAccounts && id) {
            return this.findWaBusinessAccounts(id).wabaName;
        }

        return '';
    }

    waIntegrationsDisplay(id: string): string {
        if (this.waIntegrations && id) {
            return this.findWaIntegrations(id).integrationName;
        }

        return '';
    }

    waTemplatesDisplay(id: string): string {
        if (this.waTemplates && id) {
            const template: WaTemplate = this.findWaTemplate(id);

            return `(${template.language?.shortName}) ${template.name}`;
        }

        return '';
    }

    private isValidControl(name: string, control: UntypedFormControl, find: Function) {
        const templateData: any = find.apply(this, [control.value]);
        const addInvalidError = !control?.errors?.required;
        if (isEmpty(templateData) && addInvalidError) {
            control.setErrors({'invalidId': `Invalid ${name}`, ...control.errors});
            return;
        }

        if (control.hasError('invalidId')) {
            delete control.errors['invalidId'];
        }
    }

    private findWaBusinessAccounts(id: string): WaBusinessAccounts {
        return this.waBusinessAccounts?.find(x => x.id === id);
    }

    private findWaIntegrations(id: string): WaIntegrations {
        return this.waIntegrations?.find(x => x.integrationId === id);
    }

    private findWaTemplate(id: string): WaTemplate {
        return this.waTemplates?.find(x => x.id === id);
    }

    public setImage(event: any): void {
        this.templateContentForm?.get('mediaVar').setValue(event);
    }

    public onFocusIn(element: HTMLDivElement, auto: MatAutocomplete, controlName: string) {

        switch (controlName) {
            case 'businessAccountId':
                this.focusAccountDropdown(auto);
                break;
            case 'integrationId':
                this.focusIntegrationDropdown(auto);
                break;
            case 'templateId':
                this.focusTemplateDropdown(auto);
                break;
        }

        // prevent node form scrolling when dropdown open
        element.style.overflowY = 'hidden';
    }

    private focusAccountDropdown(auto: MatAutocomplete) {
        if (this.actionParamsForm.get('businessAccountId').value.length) {
            const foundId = this.actionParamsForm.get('businessAccountId').value;
            const foundAccount = this.waBusinessAccountsFiltered.find(account => account.id === foundId);
            const accountIndex = this.waBusinessAccountsFiltered.indexOf(foundAccount);
            this.setDropdownScroll(auto, accountIndex);
        }
    }

    private focusIntegrationDropdown(auto: MatAutocomplete) {
        if (this.actionParamsForm.get('integrationId').value.length) {
            const foundIntegrationId = this.actionParamsForm.get('integrationId').value;
            const foundIntegration = this.waIntegrationsFiltered.find(integration => integration.integrationId === foundIntegrationId);
            const integrationIndex = this.waIntegrationsFiltered.indexOf(foundIntegration);
            this.setDropdownScroll(auto, integrationIndex);
        }
    }

    private focusTemplateDropdown(auto: MatAutocomplete) {
        if (this.actionParamsForm.get('templateId').value.length) {
            const foundTemplateId = this.actionParamsForm.get('templateId').value;
            const foundTemplate = this.waTemplatesFiltered.find(template => template.id === foundTemplateId);
            const templateIndex = this.waTemplatesFiltered.indexOf(foundTemplate);
            this.setDropdownScroll(auto, templateIndex);
        }
    }

    private setDropdownScroll(auto: MatAutocomplete, accountIndex: number) {
        const matOptionHeight = 48;
        const matOption = auto.options.get(accountIndex) as MatOption;

        setTimeout(() => {
            matOption.focus();

            // matOption.focus() doesnt always scroll to the correct position
            auto._setScrollTop(matOptionHeight * accountIndex);
        }, 0);
    }

    public onFocusOut(element: HTMLDivElement) {
        // prevent node form scrolling when dropdown open
        element.style.overflowY = 'auto';
    }

    private setMediaType(): void {
        if (this.templateContentForm) {
            const control = this.templateContentForm.get('mediaVar');
            if (control) {
                const val: string = control.value;
                if (val.startsWith('[[') && val.endsWith(']]')) {
                    this.selectedMediaType = 'variable';
                    return;
                }
            }
        }
        this.selectedMediaType = 'assets';
    }

    public mediaTypeChange(event: MatButtonToggleGroup): void {
        if (this.templateContentForm.get('mediaVar')) {
            this.templateContentForm.get('mediaVar').patchValue('');
            if (event.value === 'variable') {
                this.templateContentForm.get('mediaVar').setValidators([isValidVariable(), required('Media')]);
            } else {
                this.templateContentForm.get('mediaVar').setValidators([required('Media')]);
            }
        }
        this.selectedMediaType = event.value;
    }

    public clearWaIntegrations(): void {
        this.integrationId.setValue('');
        this.templateId?.setValue('');

        this.waTemplates = [];
        this.waIntegrations = [];

        this.waTemplatesFiltered = [];
        this.waIntegrationsFiltered = [];

        this.integrationsError = false;
        this.templatesError = false;
    }

    public clearWaTemplate(): void {
        this.templateId?.setValue('');

        this.waTemplates = [];
        this.waTemplatesFiltered = [];

        this.templatesError = false;
    }

    setLocation(locationMessage: LocationMessage) {

        const location = this.templateContentForm.get('location');

        location.get('locationName').setValue(locationMessage.locationName);
        location.get('address').setValue(locationMessage.locationAddress);
        location.get('latitude').setValue(locationMessage.latitude);
        location.get('longitude').setValue(locationMessage.longitude);
    }

    closeLocation(_event: any) {
        this.isLocationPicker = false;
    }

    showLocationPicker() {
        this.isLocationPicker = !this.isLocationPicker;
    }

    public configModeChanged() {
        switch (this.configMode()) {
            case 'default':
                this.defaultMode();
                break;
            case 'json':
                this.jsonMode();
                break;
        }
    }

    private configMode(): 'default' | 'json' {
        return this.actionParamsForm.get('configMode').value;
    }

    private defaultMode() {
        if (!this.actionParamsForm?.get('templateId')) {
            const {editFlows} = getEditTypes(this.canEdit);
            this.actionParamsForm.addControl('templateId', new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }, [required('Template')]));
        }
        this.templateContentForm = undefined;
    }

    private jsonMode() {
        const {editFlows} = getEditTypes(this.canEdit);
        if (this.actionParamsForm.get('templateId')) {
            this.actionParamsForm.removeControl('templateId');
        }

        this.templateContentForm = SendTemplateNode.buildJsonTemplate(this.canEdit);
    }

}
